/**
 * MainLayout — UVP 主布局（侧边栏 + 顶部栏）
 * Vercel 风格重构：动态主题切换
 */
import {
  Layout,
  Menu,
  Avatar,
  Dropdown,
  Badge,
  Switch,
  Space,
  Typography,
  Button,
  Select,
  Tag,
  message,
} from 'antd';
import {
  DashboardOutlined,
  DatabaseOutlined,
  ApiOutlined,
  BellOutlined,
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  SunOutlined,
  MoonOutlined,
  SafetyOutlined,
  TeamOutlined,
  ClockCircleOutlined,
  HistoryOutlined,
  AppstoreOutlined,
  GlobalOutlined,
  SafetyCertificateOutlined,
  OrderedListOutlined,
} from '@ant-design/icons';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import type { FC } from 'react';
import { useWorkspaceList } from '@/features/workspace/useWorkspace';
import { useTheme } from '@/contexts/ThemeContext';
import { useWorkspaceId } from '@/contexts/WorkspaceContext';
import { useQueryClient } from '@tanstack/react-query';
import { MinimalULogo } from '@/components/Logo';

const { Sider, Header, Content } = Layout;

// ---- WorkspaceSwitcher ----
const WorkspaceSwitcher: FC = () => {
  const { data: workspaces, isError } = useWorkspaceList();
  const { workspaceId: currentId, setWorkspaceId } = useWorkspaceId();
  const queryClient = useQueryClient();

  return (
    <Select
      value={currentId || undefined}
      disabled={isError}
      status={isError ? 'error' : undefined}
      onChange={v => {
        setWorkspaceId(v);
        void queryClient.invalidateQueries();
      }}
      style={{ minWidth: 160 }}
      placeholder={isError ? '工作区加载失败' : '选择工作区'}
      options={workspaces?.map(w => ({ value: w.workspaceId, label: w.workspaceName }))}
      suffixIcon={<GlobalOutlined />}
      notFoundContent={isError ? '工作区加载失败' : undefined}
    />
  );
};

// ---- MainLayout ----
const MainLayout: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark, toggleTheme } = useTheme();
  const { data: workspaces } = useWorkspaceList();
  const { workspaceId: currentWsId } = useWorkspaceId();
  const currentWs = workspaces?.find(w => w.workspaceId === currentWsId);

  // Vercel 风格颜色
  const colors = isDark
    ? {
        siderBg: '#000000',
        menuBg: '#000000',
        logoText: '#ffffff',
        headerBg: '#000000',
        headerBorder: '1px solid rgba(255,255,255,0.1)',
        contentBg: '#000000',
        textPrimary: '#e0e0e0',
        textSecondary: 'rgba(255,255,255,0.65)',
        iconColor: 'rgba(255,255,255,0.85)',
        switchThumb: '#ffffff',
        switchTrack: '#171717',
        avatarBg: '#171717',
        tagBg: 'rgba(255,255,255,0.1)',
        tagText: '#e0e0e0',
      }
    : {
        siderBg: '#ffffff',
        menuBg: '#ffffff',
        logoText: '#171717',
        headerBg: '#ffffff',
        headerBorder: '1px solid #ebebeb',
        contentBg: '#ffffff',
        textPrimary: '#171717',
        textSecondary: '#666666',
        iconColor: '#666666',
        switchThumb: '#ffffff',
        switchTrack: '#ebebeb',
        avatarBg: '#171717',
        tagBg: '#fafafa',
        tagText: '#666666',
      };

  // 菜单结构
  const menuItems = [
    { key: '/', icon: <DashboardOutlined />, label: '仪表盘' },
    {
      key: `/workspaces/${currentWsId}/unified-dashboard`,
      icon: <DashboardOutlined />,
      label: '统一工作台',
    },
    {
      key: 'batch',
      type: 'submenu' as const,
      icon: <DatabaseOutlined />,
      label: 'Batch 对比',
      children: [
        { key: '/batch/assets', icon: <AppstoreOutlined />, label: '配置管理' },
        { key: '/batch/runs', icon: <HistoryOutlined />, label: '运行记录' },
        {
          key: `/workspaces/${currentWsId}/schedules`,
          icon: <ClockCircleOutlined />,
          label: '定时调度',
        },
      ],
    },
    {
      key: 'workspace',
      type: 'submenu' as const,
      icon: <TeamOutlined />,
      label: '工作区',
      children: [
        { key: '/workspaces', icon: <AppstoreOutlined />, label: '我的工作区' },
        {
          key: `/workspaces/${currentWsId}/settings`,
          icon: <SettingOutlined />,
          label: '成员管理',
        },
        {
          key: `/workspaces/${currentWsId}/approvals`,
          icon: <SafetyCertificateOutlined />,
          label: '审批管理',
        },
      ],
    },
    {
      key: 'api-comparison',
      type: 'submenu' as const,
      icon: <ApiOutlined />,
      label: 'API 对比',
      children: [
        { key: '/comparison/api/endpoints', icon: <ApiOutlined />, label: '端点管理' },
        { key: '/comparison/api/suites', icon: <OrderedListOutlined />, label: '测试集' },
        { key: '/comparison/api/templates', icon: <AppstoreOutlined />, label: '模板管理' },
      ],
    },
    { key: '/settings/tolerance-rules', icon: <SafetyOutlined />, label: '容差规则' },
  ];

  const userMenu = {
    items: [
      {
        key: 'profile',
        icon: <UserOutlined />,
        label: '个人信息',
        onClick: () => message.info('个人信息功能开发中'),
      },
      {
        key: 'settings',
        icon: <SettingOutlined />,
        label: '设置',
        onClick: () => message.info('设置功能开发中'),
      },
      { type: 'divider' as const },
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: '退出登录',
        danger: true,
        onClick: () => {
          localStorage.removeItem('uvp_access_token');
          localStorage.removeItem('uvp_refresh_token');
          navigate('/login');
        },
      },
    ],
  };

  const selectedKey = location.pathname;
  const openKeys = ['batch', 'workspace', 'api-comparison'];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* ---- 侧边栏 ---- */}
      <Sider
        width={220}
        theme={isDark ? 'dark' : 'light'}
        collapsible
        breakpoint="lg"
        style={{ background: colors.siderBg }}
      >
        {/* Logo 区域 - Vercel 风格 */}
        <div
          style={{
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderBottom: colors.headerBorder,
            gap: 10,
          }}
        >
          <MinimalULogo size={28} />
          <Typography.Text
            style={{
              fontSize: 16,
              fontWeight: 600,
              letterSpacing: '-0.5px',
              color: colors.logoText,
              fontFamily: "Geist, -apple-system, BlinkMacSystemFont, 'Segoe UI', Inter, sans-serif",
            }}
          >
            uvp
          </Typography.Text>
        </div>
        <Menu
          theme={isDark ? 'dark' : 'light'}
          mode="inline"
          selectedKeys={[selectedKey]}
          defaultOpenKeys={openKeys}
          items={menuItems}
          onClick={({ key }) => navigate(key)}
          style={{ background: colors.menuBg, borderRight: 'none', paddingTop: 8 }}
        />
      </Sider>

      <Layout>
        {/* ---- 顶部栏 ---- */}
        <Header
          style={{
            background: colors.headerBg,
            padding: '0 24px',
            borderBottom: colors.headerBorder,
            display: 'flex',
            alignItems: 'center',
            gap: 16,
          }}
        >
          {/* Workspace 切换器 */}
          <WorkspaceSwitcher />

          {/* 当前 Workspace 角色标签 */}
          {currentWs?.currentUserRole && (
            <Tag style={{ background: colors.tagBg, color: colors.tagText, border: 'none' }}>
              {currentWs.currentUserRole}
            </Tag>
          )}

          {/* 撑满剩余空间 */}
          <div style={{ flex: 1 }} />

          {/* 主题切换 - Vercel 风格 */}
          <Space>
            <SunOutlined style={{ color: colors.textSecondary }} />
            <Switch
              size="small"
              checked={isDark}
              onChange={toggleTheme}
              style={{
                background: isDark ? colors.switchTrack : colors.switchTrack,
              }}
            />
            <MoonOutlined style={{ color: colors.textSecondary }} />
          </Space>

          {/* 通知 */}
          <Badge count={0} size="small">
            <Button type="text" icon={<BellOutlined style={{ color: colors.iconColor }} />} />
          </Badge>

          {/* 用户菜单 */}
          <Dropdown menu={userMenu} placement="bottomRight">
            <Space style={{ cursor: 'pointer' }}>
              <Avatar
                size="small"
                icon={<UserOutlined />}
                style={{ background: colors.avatarBg, color: '#fff' }}
              />
              <Typography.Text style={{ color: colors.textPrimary }}>
                {localStorage.getItem('uvp_username') ?? '用户'}
              </Typography.Text>
            </Space>
          </Dropdown>
        </Header>

        {/* ---- 主内容区 ---- */}
        <Content style={{ padding: 24, background: colors.contentBg }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
